import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { usePolling } from 'Hooks';
import { BiSitemap } from 'react-icons/bi';
import {
  listMaskingJobGroups,
  createMaskingJobGroup,
  updateMaskingJobGroup,
  replaceMaskingJobGroupSteps,
  deleteMaskingJobGroup,
  runMaskingJobGroup,
  cancelMaskingJobGroupRun,
} from 'Secure/api';
import GroupCard from './GroupCard';
import GroupDialog from './GroupDialog';
import { stepPagePath } from './AddStepPage';
import { GROUP_TYPES, encodeGroupType, decodeGroupType, typeLabel } from './groupTypes';

// Orchestration Groups (mockup 8a, formerly "Job Groups", then "Fleet Masking"): masking jobs bundled into a strict serial run order and executed
// as one unit. Steps are (data source + inventory + optional destination) tuples; the
// add-step page (AddStepPage, /secure/groups/:id/step) accepts multiple destinations at
// once, so a fan-out (one source → many targets) is built there as one step per
// destination. A failed step halts the group.
// TODO(cross-repo): true single-job fan-out via the masking-service `targetConnections`
// contract (read once, mask once, write N targets in parallel) — needs platform-api
// plumbing; tracked in the umbrella TODO.
function JobGroups() {
  const { setNotification } = useContext(AppContext);
  const history = useHistory();
  const location = useLocation();
  const [groups, setGroups] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);
  // The add-step page hands back the group it changed so it lands expanded.
  const [expandedGroupId, setExpandedGroupId] = useState(
    (location.state && location.state.expandGroupId) != null ? location.state.expandGroupId : null
  );
  // Dialogs: {mode: 'create'} | {mode: 'edit', group} for the group dialog.
  const [groupDialog, setGroupDialog] = useState(null);
  const [typeFilter, setTypeFilter] = useState('ALL');

  const loadGroups = useCallback(async () => {
    try {
      const result = await listMaskingJobGroups();
      setGroups(Array.isArray(result) ? result : []);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setFetchingData(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const anyRunActive = useMemo(() => groups.some((g) => g.runActive), [groups]);
  usePolling(loadGroups, anyRunActive ? 5000 : null);

  const totalSteps = useMemo(
    () => groups.reduce((sum, g) => sum + ((g.steps && g.steps.length) || 0), 0),
    [groups]
  );

  // The type marker rides inside description (see groupTypes.js); everything below the
  // fetch layer works with decoded groups: clean description + typeKey.
  const decodedGroups = useMemo(
    () => groups.map((g) => ({ ...g, ...decodeGroupType(g.description) })),
    [groups]
  );
  const typeCounts = useMemo(() => {
    const counts = {};
    decodedGroups.forEach((g) => {
      counts[g.typeKey] = (counts[g.typeKey] || 0) + 1;
    });
    return counts;
  }, [decodedGroups]);
  const visibleGroups = useMemo(
    () =>
      typeFilter === 'ALL'
        ? decodedGroups
        : decodedGroups.filter((g) => g.typeKey === typeFilter),
    [decodedGroups, typeFilter]
  );

  const notifyError = useCallback(
    (header, e) => {
      const body =
        (e && e.response && e.response.data && e.response.data.message) ||
        (e && e.message) ||
        'Unexpected error.';
      setNotification({ header, body });
    },
    [setNotification]
  );

  const withRefresh = useCallback(
    (header) => async (action) => {
      try {
        await action();
      } catch (e) {
        notifyError(header, e);
      }
      await loadGroups();
    },
    [loadGroups, notifyError]
  );

  const handleRun = (group) =>
    withRefresh('Run group failed')(() => runMaskingJobGroup(group.id));

  const handleCancel = (group) =>
    withRefresh('Cancel failed')(() => cancelMaskingJobGroupRun(group.id));

  const handleDelete = (group) => {
    if (!window.confirm(`Delete group "${group.name}"? This cannot be undone.`)) return;
    withRefresh('Delete failed')(() => deleteMaskingJobGroup(group.id));
  };

  // Steps replace covers reorder, remove, and append: send the whole list in the new order.
  const saveSteps = (group, steps) =>
    withRefresh('Step update failed')(() =>
      replaceMaskingJobGroupSteps(
        group.id,
        steps.map((s) => ({
          dataSourceId: s.dataSourceId,
          inventoryId: s.inventoryId,
          targetDataSourceId: s.targetDataSourceId != null ? s.targetDataSourceId : undefined,
        }))
      )
    );

  // Adding / editing a masking job is a full page (pickers left, live destination review
  // right): append opens it bare, editing a block passes the block's step ids. The page
  // splices the result into the group's steps itself and navigates back here.
  const openStepPage = (group, editSteps) => history.push(stepPagePath(group.id, editSteps));

  const handleSaveGroup = async ({ name, description, typeKey }) => {
    const dialog = groupDialog;
    setGroupDialog(null);
    const encoded = encodeGroupType(description, typeKey);
    if (dialog && dialog.mode === 'edit') {
      await withRefresh('Group update failed')(() =>
        updateMaskingJobGroup(dialog.group.id, { name, description: encoded })
      );
    } else {
      await withRefresh('Group create failed')(async () => {
        const created = await createMaskingJobGroup({ name, description: encoded });
        if (created && created.id != null) setExpandedGroupId(created.id);
      });
    }
  };


  return (
    <div>
      <FetchContainer error={error} fetchingData={fetchingData} absolute={true} fluid>
        <PageHeader
          eyebrow='Secure'
          header='Orchestration Groups'
          icon={BiSitemap}
          tooltip='Group masking jobs to run serially in a fixed order, as one unit — including fan-outs (one source, several destinations).'
          meta={
            <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
              {groups.length} group{groups.length === 1 ? '' : 's'} · {totalSteps} job
              {totalSteps === 1 ? '' : 's'} grouped
            </span>
          }
          primaryAction={
            <Dropdown>
              <Dropdown.Toggle size='sm' variant='primary' data-testid='job-groups-new'>
                + New
              </Dropdown.Toggle>
              <Dropdown.Menu align='right'>
                {GROUP_TYPES.map((t) => (
                  <Dropdown.Item
                    key={t.key}
                    data-testid={`job-groups-new-${t.key}`}
                    onClick={() => setGroupDialog({ mode: 'create', typeKey: t.key })}
                  >
                    {t.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          }
        />
        <div className='d-flex align-items-center flex-wrap mb-3' style={{ gap: 8 }}>
          {[{ key: 'ALL', label: 'All' }, ...GROUP_TYPES].map((t) => {
            const active = typeFilter === t.key;
            const count = t.key === 'ALL' ? decodedGroups.length : typeCounts[t.key] || 0;
            return (
              <button
                key={t.key}
                type='button'
                data-testid={`job-groups-filter-${t.key}`}
                onClick={() => setTypeFilter(t.key)}
                className='sg-pill sg-pill--nodot'
                style={{
                  cursor: 'pointer',
                  border: active ? '1px solid var(--brand-blue, #315DFF)' : '1px solid var(--ink-150)',
                  background: active ? 'var(--brand-blue, #315DFF)' : 'var(--ink-50)',
                  color: active ? '#fff' : 'var(--ink-500)',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {t.label} ({count})
              </button>
            );
          })}
        </div>
        {visibleGroups.length === 0 && !fetchingData ? (
          <div className='sg-card p-4 text-center' style={{ color: 'var(--ink-500)' }}>
            {decodedGroups.length === 0 ? (
              <>
                <div className='mb-2'>No groups yet.</div>
                <div className='sg-small'>
                  Create a group, then add masking jobs (data source + inventory, with one or
                  several destinations) to run them serially as one unit.
                </div>
              </>
            ) : (
              <div>No {typeLabel(typeFilter)} groups yet.</div>
            )}
          </div>
        ) : (
          visibleGroups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              expanded={expandedGroupId === group.id}
              onToggle={() =>
                setExpandedGroupId(expandedGroupId === group.id ? null : group.id)
              }
              onRun={() => handleRun(group)}
              onCancel={() => handleCancel(group)}
              onDelete={() => handleDelete(group)}
              onEdit={() => setGroupDialog({ mode: 'edit', group })}
              onReplaceSteps={(steps) => saveSteps(group, steps)}
              onAddStep={() => openStepPage(group)}
              onEditStep={(blockSteps) => openStepPage(group, blockSteps)}
            />
          ))
        )}
      </FetchContainer>
      <GroupDialog
        show={!!groupDialog}
        mode={groupDialog ? groupDialog.mode : 'create'}
        group={groupDialog && groupDialog.group}
        presetTypeKey={groupDialog && groupDialog.typeKey}
        onHide={() => setGroupDialog(null)}
        onSave={handleSaveGroup}
      />
    </div>
  );
}

export default JobGroups;
